import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Calendar, Package, CheckCircle, Sparkles } from 'lucide-react';
import { mockMarketData } from '../mock';

export const MarketSection = ({ t, lang }) => {
  const stats = [
    {
      icon: TrendingUp,
      label: t.market.avgPrice,
      value: mockMarketData.avgPrice[lang],
      change: mockMarketData.growth,
      gradient: 'from-[#C5A05E] to-[#D4B06E]'
    },
    {
      icon: Calendar,
      label: t.market.daysOnMarket,
      value: mockMarketData.daysOnMarket,
      change: '-12%',
      gradient: 'from-[#D4B06E] to-[#C5A05E]'
    },
    {
      icon: Package,
      label: t.market.inventory,
      value: '234',
      change: mockMarketData.inventoryChange,
      gradient: 'from-[#B39050] to-[#C5A05E]'
    }
  ];

  return (
    <section id="market" className="py-32 bg-black relative overflow-hidden">
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(197, 160, 94, 0.1) 2px, transparent 2px),
            linear-gradient(90deg, rgba(197, 160, 94, 0.1) 2px, transparent 2px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridSlide 20s linear infinite'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 space-y-6">
          <div className="inline-flex items-center gap-3 px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider mb-4 animate-fadeInUp">
            <Sparkles className="w-4 h-4" />
            Market Intelligence
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {t.market.title}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full animate-pulse" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            {t.market.subtitle}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group relative animate-fadeInUp"
              style={{ animationDelay: `${0.4 + index * 0.1}s`, opacity: 0, animationFillMode: 'forwards' }}
            >
              {/* Glowing effect */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${stat.gradient} opacity-0 group-hover:opacity-30 blur-2xl transition-opacity duration-500 rounded-3xl`} />
              
              <Card className="relative backdrop-blur-xl bg-white/5 border border-white/10 hover:border-white/20 overflow-hidden transition-all duration-500 hover:transform hover:scale-105">
                {/* Top gradient line */}
                <div className={`h-1.5 bg-gradient-to-r ${stat.gradient}`} />
                
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${stat.gradient} p-[2px] group-hover:scale-110 transition-transform duration-500`}>
                      <div className="w-full h-full bg-black/90 backdrop-blur-xl rounded-2xl flex items-center justify-center">
                        <stat.icon className="w-7 h-7 text-[#C5A05E]" />
                      </div>
                    </div>
                    <span className={`px-4 py-1.5 rounded-full text-sm font-bold backdrop-blur-xl ${
                      stat.change.startsWith('+')
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-white group-hover:text-[#C5A05E] transition-colors duration-300">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400 font-medium uppercase tracking-wider">{stat.label}</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Key Insights */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.7s', opacity: 0, animationFillMode: 'forwards' }}>
          <div className="relative">
            {/* Glowing background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#C5A05E]/10 via-[#D4B06E]/10 to-[#C5A05E]/10 rounded-3xl blur-2xl" />
            
            <Card className="relative backdrop-blur-2xl bg-white/5 border border-white/10">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-white flex items-center gap-3">
                  <div className="w-1.5 h-10 bg-gradient-to-b from-[#C5A05E] to-transparent rounded-full" />
                  {t.market.insights.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  t.market.insights.point1,
                  t.market.insights.point2,
                  t.market.insights.point3
                ].map((insight, index) => (
                  <div 
                    key={index} 
                    className="group flex items-start gap-4 p-6 backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 hover:border-[#C5A05E]/50 hover:bg-white/10 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-gray-300 leading-relaxed flex-1 pt-1 group-hover:text-white transition-colors">{insight}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gridSlide {
          0% { background-position: 0 0; }
          100% { background-position: 50px 50px; }
        }
      `}</style>
    </section>
  );
};